import type { SmartLinkProviderKey } from "./smart-links";

/**
 * Workspace-entity ref types, insertable from the `@` popup. `staged_folder`
 * is a declared staged folder from the agent version's config, resolved to
 * its sandbox path at prompt-render time (the ref id is the folder name).
 * Staged-folder entries are supplied by the caller's `search` source (drawn
 * from the draft config), never by the server taggables route.
 *
 * `port` is the RETIRED sources/destinations chip. It is never offered for
 * insertion; existing documents that still carry one render it as a neutral
 * tag and the editor flags it. DELETE in Milestone 4 of
 * docs/exec-plans/active/staged-folders-retire-sources-destinations.md.
 */
export type EntityTaggableType =
  | "staged_folder"
  | "agent"
  | "tool"
  | "file"
  | "custom_model"
  | "credential"
  | "skill"
  | "integration";

/** Every {@link EntityTaggableType}, in popup display order. Single source of truth. */
export const ENTITY_TAGGABLE_TYPES = [
  "staged_folder",
  "agent",
  "tool",
  "file",
  "custom_model",
  "credential",
  "skill",
  "integration",
] as const;

/**
 * The kind of entity a reference chip points at: a taggable workspace entity
 * (`@` popup) or a smart-link provider (paste-inserted URL chips; see
 * ./smart-links.ts).
 */
export type RetiredEntityRefType = "port";
export const RETIRED_ENTITY_REF_TYPE: RetiredEntityRefType = "port";

export type EntityRefType = EntityTaggableType | RetiredEntityRefType | SmartLinkProviderKey;

/**
 * Per-type presentation for the rendered chip: text color, background tint, and
 * the inner markup of a 24×24 lucide icon used as a CSS mask. Pure data (no
 * runtime imports) so it stays server-safe and drives the injected CSS.
 */
export interface EntityRefStyle {
  /** CSS `color` for the chip text and icon. */
  color: string;
  /** CSS `background` tint behind the chip. */
  background: string;
  /** Inner SVG markup (paths/circles) of the type's lucide icon. */
  iconSvg: string;
}

/** A resolved entity reference embedded in an entity chip document. */
export interface EntityRef {
  /** Stable id: agt_*, tool name, fil_*, cmdl_*, vlc_*, skl_*, integration key,
   *  or — for `sharepoint` refs — the pasted SharePoint/OneDrive URL itself. */
  id: string;
  type: EntityRefType;
  /** Display label captured at insert time (re-resolved on render/serialize). */
  label: string;
  /**
   * The field a `custom_model` ref points at, when it names ONE PROPERTY
   * rather than the whole model. `null`/absent means the whole model. A
   * custom model is a single table, so this is one segment, never a path.
   *
   * Carried VERBATIM off the document, including a malformed value —
   * untrimmed and uncapped. It is deliberately not filtered here: a chip
   * whose field is garbage must stay a (broken) field reference so validation
   * can flag it, because silently treating it as a whole-model reference
   * widens what the instruction means while showing the author a green chip.
   * Shape is enforced in exactly two places, on the same predicate, so they
   * cannot disagree: `isCustomModelFieldRef` gates what reaches the
   * model-facing tag, and `resolveCustomModelFieldRef` explains to the author
   * why the same value was refused. See `ChipRefAttrs.field` in `@quovy/ui`.
   */
  field?: string | null;
}
